'use client';

import { forwardRef } from 'react';
import { motion } from 'motion/react';
import { cls, cn } from '@/lib/utils';
import type {
  TypographyH1Props,
  TypographyH2Props,
  TypographyH3Props,
  TypographyH4Props,
  TypographyParagraphProps,
  TypographyBlockquoteProps,
  TypographyLargeProps,
  TypographySmallProps,
} from './types';

//
// Typography H1
//
const TypographyH1 = forwardRef<HTMLHeadingElement, TypographyH1Props>(
  ({ className, children, ...properties }, reference) => (
    <motion.h1
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          ~text-2xl/6xl
          font-headline font-medium
          ~leading-tight/[1.2]
        `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h1>
  ),
);

TypographyH1.displayName = 'TypographyH1';

//
// Typography H2
// Used as Headline
//
const TypographyH2 = forwardRef<HTMLHeadingElement, TypographyH2Props>(
  ({ className, children, ...properties }, reference) => (
    <motion.h2
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          ~text-2xl/4xl
          leading-tight lg:leading-[1.3]
          font-headline font-semibold
          tracking-tight
        `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h2>
  ),
);

TypographyH2.displayName = 'TypographyH2';

//
// Typography H3
//
const TypographyH3 = forwardRef<HTMLHeadingElement, TypographyH3Props>(
  ({ className, children, ...properties }, reference) => (
    <motion.h3
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          ~text-xl/3xl
          font-headline font-semibold
          tracking-wide
        `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h3>
  ),
);

TypographyH3.displayName = 'TypographyH3';

//
// Typography H4
// Used as Header
//
const TypographyH4 = forwardRef<HTMLHeadingElement, TypographyH4Props>(
  ({ className, children, ...properties }, reference) => (
    <motion.h4
      ref={reference}
      className={cn(
        cls`
          scroll-m-20
          ~text-sm/lg
          leading-snug lg:leading-[1.4]
          font-header font-bold
          uppercase tracking-wide
        `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.h4>
  ),
);

TypographyH4.displayName = 'TypographyH4';

//
// Typography Paragraph
//
const TypographyP = forwardRef<HTMLParagraphElement, TypographyParagraphProps>(
  ({ className, children, ...properties }, reference) => (
    <motion.p
      ref={reference}
      className={cn(
        cls`
        ~leading-6/9
        ~text-base/2xl
        font-body
      `,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.p>
  ),
);

TypographyP.displayName = 'TypographyP';

//
// Typography Blockquote
//
const TypographyBlockquote = forwardRef<
  HTMLQuoteElement,
  TypographyBlockquoteProps
>(({ className, children, ...properties }, reference) => (
  <motion.blockquote
    ref={reference}
    className={cn(
      cls`
        mt-6 pl-4
        border-l-2
        font-body italic
        ~text-base/xl
      `,
      className,
    )}
    {...properties}
  >
    {children}
  </motion.blockquote>
));

TypographyBlockquote.displayName = 'TypographyBlockquote';

//
// Typography Large
//
const TypographyLarge = forwardRef<HTMLDivElement, TypographyLargeProps>(
  ({ className, children, ...properties }, reference) => (
    <motion.div
      ref={reference}
      className={cn(cls`font-body font-semibold ~text-base/xl`, className)}
      {...properties}
    >
      {children}
    </motion.div>
  ),
);

TypographyLarge.displayName = 'TypographyLarge';

//
// Typography Small
//
const TypographySmall = forwardRef<HTMLDivElement, TypographySmallProps>(
  ({ className, children, ...properties }, reference) => (
    <motion.small
      ref={reference}
      className={cn(
        cls`text-sm sm:text-base font-body font-medium leading-none`,
        className,
      )}
      {...properties}
    >
      {children}
    </motion.small>
  ),
);

TypographySmall.displayName = 'TypographySmall';

export {
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
  TypographyBlockquote,
  TypographyLarge,
  TypographySmall,
};
